import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import Metric from '@components/shared/Metric';
import BrochureDownloadLink from '@components/shared/BrochureDownloadLink/BrochureDownloadLink';
import useAnalytics from '@root/shared/useAnalytics';
import type { Brochure, Building } from '@root/types';
import ExpandableDescription from '@components/shared/ExpandableDescription';
import SpaceProviderInfo from '@components/shared/SpaceProviderInfo';
import { useFlags } from 'launchdarkly-react-client-sdk';
import cn from 'classnames';
import NotableTenants from '../NotableTenants';
import s from './BasicInfo.module.less';

type Props = {
  building: Building;
};

const Separator = ({
  pipe = false,
  alwaysShow = false,
}: {
  pipe?: boolean;
  alwaysShow?: boolean;
}) => (
  <span
    className={cn(
      'inline-block px-1 py-0',
      pipe && 'w-2.5  text-black-010',
      alwaysShow && 'mobile:inline',
    )}
  >
    {pipe ? '|' : '-'}
  </span>
);

const BasicInfo = ({ building }: Props) => {
  const { t } = useTranslation('building');
  const { brochureInteraction, PARAMETERS } = useAnalytics();
  const {
    numberOfFloors,
    rentableBuildingArea,
    yearRenovated,
    yearBuilt,
    owningCompany,
    brochures = [],
    address,
  } = building;

  const flags = useFlags();
  const buildingBrandingEnabled = flags['outreach.branding-building'];

  const renderBasicInfoList = () => (
    <div>
      <ul
        className={cn(
          s.infoContainer,
          buildingBrandingEnabled && 'desktop:!gap-x-12 tablet:!gap-x-4',
        )}
        data-testid="basic-building-info"
      >
        {/* scratch-code to remove w/ industrial-proof-of-concept */}
        {building.isIndustrial ? (
          <li>
            <Metric
              label={t('details.propertyType')}
              value={building.assetTypes.map(assetType => t(`common:${assetType}`)).join(', ')}
            />
          </li>
        ) : null}
        {numberOfFloors ? (
          <li className={cn(!buildingBrandingEnabled && 'desktop:!basis-1/4 tablet:!basis-1/4')}>
            <Metric
              label={t('details.floors')}
              labelClassName={cn(buildingBrandingEnabled && 'font-body-medium')}
              className={cn(buildingBrandingEnabled && 'font-subtitle-de-emphasis')}
              value={numberOfFloors}
            />
          </li>
        ) : null}
        {rentableBuildingArea ? (
          <li className={cn(!buildingBrandingEnabled && 'desktop:!basis-1/4 tablet:!basis-1/4')}>
            <Metric
              label={t('details.rentableBuildingArea')}
              labelClassName={cn(buildingBrandingEnabled && 'font-body-medium')}
              className={cn(buildingBrandingEnabled && 'font-subtitle-de-emphasis')}
              value={t('units:area', { area: rentableBuildingArea })}
            />
          </li>
        ) : null}
        {yearRenovated ? (
          <li className={cn(!buildingBrandingEnabled && 'desktop:!basis-1/4 tablet:!basis-1/4')}>
            <Metric
              labelClassName={cn(buildingBrandingEnabled && 'font-body-medium')}
              className={cn(buildingBrandingEnabled && 'font-subtitle-de-emphasis')}
              value={yearRenovated}
              label={t('details.yearRenovated')}
            />
          </li>
        ) : null}
        {yearBuilt ? (
          <li className={cn(!buildingBrandingEnabled && 'desktop:!basis-1/4 tablet:!basis-1/4')}>
            <Metric
              labelClassName={cn(buildingBrandingEnabled && 'font-body-medium')}
              className={cn(buildingBrandingEnabled && 'font-subtitle-de-emphasis')}
              value={yearBuilt}
              label={t('details.yearBuilt')}
            />
          </li>
        ) : null}
        {/* scratch-code to remove w/ industrial-proof-of-concept */}
        {building.isIndustrial ? (
          <>
            {building.driveInDoors ? (
              <li>
                <Metric label={t('details.driveInDoors')} value={building.driveInDoors} />
              </li>
            ) : null}
            {building.gradeLevelDoors ? (
              <li>
                <Metric label={t('details.gradeLevelDoors')} value={building.gradeLevelDoors} />
              </li>
            ) : null}
            {building.dockHighDoors ? (
              <li>
                <Metric label={t('details.dockHighDoors')} value={building.dockHighDoors} />
              </li>
            ) : null}
            {building.fireSprinklers ? (
              <li>
                <Metric label={t('details.fireSprinklers')} value={building.fireSprinklers} />
              </li>
            ) : null}
            {building.minimumIndoorClearHeight ? (
              <li>
                <Metric
                  label={t('details.minimumIndoorClearHeight')}
                  value={t('units:length', {
                    length: {
                      magnitude: building.minimumIndoorClearHeight.magnitude,
                      unit: building.minimumIndoorClearHeight.units.length,
                    },
                  })}
                />
              </li>
            ) : null}
            {building.percentOffice ? (
              <li>
                <Metric label={t('details.percentOffice')} value={`${building.percentOffice}%`} />
              </li>
            ) : null}
            {building.parkingSpaces ? (
              <li>
                <Metric label={t('details.parkingSpaces')} value={building.parkingSpaces} />
              </li>
            ) : null}
          </>
        ) : null}
      </ul>
    </div>
  );

  return (
    <div data-testid="basic-info-container" className={s.container}>
      {!buildingBrandingEnabled && owningCompany ? (
        <SpaceProviderInfo spaceProvider={owningCompany} sourcePage={PARAMETERS.buildingPage} />
      ) : null}
      {brochures && brochures.length > 0 ? (
        <div
          className={cn(
            'mb-3 grid grid-cols-2 tablet:grid-cols-none mobile:grid-cols-none',
            buildingBrandingEnabled &&
              'border-b-[1px] border-solid border-general-neutral-tertiary pb-2 desktop:mr-[22px]',
          )}
        >
          {brochures.map((buildingBrochure: Brochure) => (
            <div
              key={buildingBrochure.url}
              className="pb-1 pr-2 desktop:pb-0 tablet:last:pb-0 mobile:last:pb-0"
            >
              <BrochureDownloadLink
                brochure={buildingBrochure}
                onClick={() =>
                  brochureInteraction({
                    sourcePage: PARAMETERS.buildingPage,
                    otherAttributes: {
                      attachmentId: buildingBrochure.id,
                      attachmentName: buildingBrochure.name,
                    },
                  })
                }
                lineWrap={2}
              />
            </div>
          ))}
        </div>
      ) : null}
      {renderBasicInfoList()}
      {buildingBrandingEnabled && building.description && (
        <div className="font-subtitle">
          {address.buildingName || address.street}
          {address.buildingName ? (
            <div className="inline">
              <Separator />
              {address.street}
            </div>
          ) : null}
        </div>
      )}
      {building.description && (
        <div className={cn(s.descriptionContainer, buildingBrandingEnabled && '!mt-1.5')}>
          <ExpandableDescription>{building.description}</ExpandableDescription>
        </div>
      )}
      {isEmpty(building.notableTenants) ? null : (
        <NotableTenants notableTenants={building.notableTenants} />
      )}
    </div>
  );
};

export default BasicInfo;
